import React, { useState } from 'react'
import { TextField, InputLabel, IconButton, InputAdornment } from '@mui/material'
import Grid from '@mui/material/Grid2'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CloseIcon from '@mui/icons-material/Close'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import { Modal } from 'src/shared/modules/modals'
import Swal from 'sweetalert2'
import './styles.scss'

interface Props {
    label: string
    value: string | number
    onChange: (value: string) => void
    maxNumber?: number
}

export const InputCustomSearchLargeNumber = ({ label, value, onChange, maxNumber = 0 }: Props) => {
  const [showModal, setShowModal] = useState(false)
  const [detailItems, setDetailItems] = useState<string[]>([])
  const [searchFilter, setSearchFilter] = useState<string>('')

  const processPastedText = (text: string): string => {
    // Procesar el texto pegado (puede venir de Excel con tabs o saltos de línea)
    // Convertir tabs y saltos de línea a comas
    return text
      .replace(/\t/g, ',') // Reemplazar tabs con comas
      .replace(/\n/g, ',') // Reemplazar saltos de línea con comas
      .replace(/\r/g, '') // Eliminar retornos de carro
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .join(',')
  }

  const validateAndSetValue = (newValue: string) => {
    // Si maxNumber es 0, no hay límite
    if (maxNumber === 0) {
      onChange(newValue)
      return
    }

    // Contar elementos
    const items = newValue
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)

    // Validar si excede el máximo
    if (items.length > maxNumber) {
      Swal.fire({
        icon: 'warning',
        title: 'Límite excedido',
        text: `El número máximo de elementos permitidos es ${maxNumber}. Se encontraron ${items.length} elementos. Se seleccionaron los primeros ${maxNumber} elementos.`,
        confirmButtonText: 'Aceptar'
      })
      // Limitar a los primeros maxNumber elementos
      const limitedItems = items.slice(0, maxNumber)
      onChange(limitedItems.join(','))
    } else {
      onChange(newValue)
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const processedText = processPastedText(pastedText)
    validateAndSetValue(processedText)
  }

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText()
      const processedText = processPastedText(text)
      validateAndSetValue(processedText)
    } catch (err) {
      console.error('Error al leer el portapapeles:', err)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndSetValue(e.target.value)
  }

  const handleOpenModal = () => {
    // Separar el valor por comas y limpiar espacios
    const items = String(value || '')
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
    
    setDetailItems(items)
    setSearchFilter('') // Limpiar el filtro al abrir el modal
    setShowModal(true)
  }

  const handleDeleteItem = (itemToDelete: string) => {
    // Encontrar el índice real del elemento en detailItems
    const itemIndex = detailItems.findIndex(item => item === itemToDelete)
    if (itemIndex !== -1) {
      const updatedItems = detailItems.filter((_, index) => index !== itemIndex)
      setDetailItems(updatedItems)
      // Actualizar el valor del input con los elementos restantes
      onChange(updatedItems.join(','))
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSearchFilter('') // Limpiar el filtro al cerrar el modal
  }

  // Filtrar elementos basado en el texto de búsqueda
  const filteredItems = detailItems.filter(item =>
    item.toLowerCase().includes(searchFilter.toLowerCase())
  )

  const handleClear = () => {
    onChange('')
  }

  return (
    <>
      <Grid className="input-search-large-form-control">
        <InputLabel className="input-search-label-left">{label}</InputLabel>
        <TextField
          type="text"
          variant="outlined"
          size="small"
          fullWidth
          className="input-search-field"
          value={value || ''}
          onChange={handleChange}
          onPaste={handlePaste}
          placeholder="Escribe o pega números separados por coma"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {value ? (
                  <>
                    <IconButton
                      size="small"
                      onClick={handleOpenModal}
                      title="Ver detalle"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={handleClear}
                      title="Limpiar"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <IconButton
                    size="small"
                    onClick={handlePasteFromClipboard}
                    title="Pegar desde portapapeles"
                  >
                    <ContentPasteIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {showModal && (
        <Modal
          title="Detalle de búsqueda"
          onClose={handleCloseModal}
        >
          <div className="modal-detail-content">
            <div className="modal-detail-header">
              <p>Total de elementos: <strong>{detailItems.length}</strong></p>
              {searchFilter && (
                <p className="filtered-count">
                  Mostrando: <strong>{filteredItems.length}</strong> de {detailItems.length}
                </p>
              )}
            </div>
            <div className="modal-search-filter">
              <TextField
                type="text"
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Buscar elementos..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchFilter && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchFilter('')}
                        title="Limpiar búsqueda"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className="modal-detail-list">
              {filteredItems.length > 0 ? (
                <ul>
                  {filteredItems.map((item, index) => (
                    <li key={index}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteItem(item)}
                        title="Eliminar elemento"
                        className="delete-item-button"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <span className="item-text">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : detailItems.length > 0 ? (
                <p className="no-results">No se encontraron elementos que coincidan con la búsqueda</p>
              ) : (
                <p>No hay elementos para mostrar</p>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
